
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { LoginForm, type LoginForm as LoginFormData } from "./LoginForm";
import { LoginErrorAlert } from "./LoginErrorAlert";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Icons } from "./ui/icons";
import { useToast } from "./ui/use-toast";
import { ResetPasswordDialog } from "./ResetPasswordDialog";

interface LoginDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSignUpClick?: () => void;
}

export const LoginDialog = ({ open, onOpenChange, onSignUpClick }: LoginDialogProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Check for password recovery session
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setShowResetPassword(true);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const handlePasswordReset = async (email: string) => {
    if (!email) {
      setError("Please enter your email address to reset your password.");
      return;
    }

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email);

      if (error) throw error;

      toast({
        title: "Check your email",
        description: "We've sent you a password reset link if an account exists with this email.",
      });
    } catch (error: any) {
      console.error('Password reset error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to send reset email. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleSignUp = () => {
    if (onSignUpClick) {
      onOpenChange(false);
      setTimeout(() => {
        onSignUpClick();
      }, 100);
    }
  };

  const handleSocialLogin = async (provider: 'google' | 'azure') => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/training`,
        },
      });
      
      if (error) throw error;
    } catch (error: any) {
      console.error('Error:', error);
      setError('Failed to sign in with social provider. Please try again.');
    }
  };

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const { data: authData, error: signInError } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });

      if (signInError) {
        // Handle specific error cases
        if (signInError.message.includes('Invalid login credentials')) {
          setError("Incorrect email or password. Please try again or reset your password.");
        } else if (signInError.message.includes('Email not confirmed')) {
          setError("Please confirm your email address before logging in. Check your inbox for the confirmation link.");
        } else {
          setError(signInError.message || "Failed to sign in. Please try again.");
        }
        return;
      }

      if (authData.user) {
        onOpenChange(false);
        navigate('/training');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Log in to your account</DialogTitle>
            <DialogDescription>
              Welcome back! Please enter your details.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4">
            <Button
              variant="outline"
              onClick={() => handleSocialLogin('google')}
              className="w-full"
              type="button"
            >
              <Icons.google className="mr-2 h-4 w-4" />
              Continue with Google
            </Button>
            
            <Button
              variant="outline"
              onClick={() => handleSocialLogin('azure')}
              className="w-full"
              type="button"
            >
              <Icons.microsoft className="mr-2 h-4 w-4" />
              Continue with Microsoft
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  Or continue with
                </span>
              </div>
            </div>
          </div>

          {error && (
            <LoginErrorAlert
              description={error}
              onSignUpClick={handleSignUp}
              onPasswordReset={() => {
                const email = document.querySelector<HTMLInputElement>('input[name="email"]')?.value || '';
                handlePasswordReset(email);
              }}
            />
          )}

          <LoginForm onSubmit={onSubmit} isLoading={isLoading} />
        </DialogContent>
      </Dialog>

      <ResetPasswordDialog 
        open={showResetPassword} 
        onOpenChange={setShowResetPassword}
        onSuccess={() => {
          setShowResetPassword(false);
          onOpenChange(false);
        }}
      />
    </>
  );
};
